import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spin, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import {
  LuminousAiQuotaDonut,
  LuminousBusinessTrendDual,
  LuminousHostCpuTrend,
  LuminousHostMemoryDonut,
} from "../components/dashboard/luminous/LuminousCharts";
import { fetchAdminFeedbackPage, fetchAdminOverview } from "../api/client";
import type { AdminFeedbackRow, AdminOverview } from "../api/types";
import { useAdminHostMetricsWs } from "../hooks/useAdminHostMetricsWs";

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}k`;
  return n.toLocaleString("zh-CN");
}

function fmtBytes(n: number): string {
  if (n >= 1 << 30) return `${(n / (1 << 30)).toFixed(1)} GiB`;
  if (n >= 1 << 20) return `${(n / (1 << 20)).toFixed(1)} MiB`;
  if (n >= 1 << 10) return `${(n / (1 << 10)).toFixed(0)} KiB`;
  return `${n} B`;
}

const hostWsStatusLabel: Record<string, string> = {
  idle: "未连接",
  connecting: "连接中…",
  open: "已连接",
  closed: "已断开",
  error: "异常",
};

export function OverviewPage() {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [feedback, setFeedback] = useState<AdminFeedbackRow[]>([]);
  const [err, setErr] = useState<"forbidden" | "error" | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const [ov, fb] = await Promise.all([
      fetchAdminOverview(),
      fetchAdminFeedbackPage({ page: 1, page_size: 2 }),
    ]);
    if (ov === "forbidden") setErr("forbidden");
    else if (ov === "error") setErr("error");
    else setData(ov);
    if (fb !== "forbidden" && fb !== "error") setFeedback(fb.items);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const trend = data?.activity_trend_30d ?? [];
  const last7 = useMemo(() => {
    const tail = trend.slice(-7);
    if (tail.length >= 7) return tail;
    const pad = 7 - tail.length;
    const out = [...tail];
    for (let i = 0; i < pad; i++) {
      out.unshift({
        date: `pad-${i}`,
        new_users: 0,
        orders_paid: 0,
        new_subscriptions: 0,
      });
    }
    return out.slice(-7);
  }, [trend]);

  const llm = data?.llm;
  const budget = llm?.monthly_token_budget;
  const usedMonth = llm?.tokens_month?.total_tokens ?? 0;
  const promptT = llm?.tokens_month?.prompt_tokens ?? 0;
  const completionT = llm?.tokens_month?.completion_tokens ?? 0;
  const sumTok = Math.max(1, promptT + completionT);

  const budgetPct =
    budget != null && budget > 0
      ? Math.min(100, (usedMonth / budget) * 100)
      : usedMonth > 0
        ? Math.min(100, 40 + (Math.log10(usedMonth + 10) / 5) * 60)
        : 0;
  const budgetValDisplay =
    budget != null && budget > 0
      ? Math.min(99.99, (usedMonth / budget))
      : null;
  const promptRatio = promptT / sumTok;
  const completionRatio = completionT / sumTok;

  const wsEnabled = !loading && err === null && data !== null;
  const {
    status: hostWsStatus,
    metrics: hostMetrics,
    series: hostSeries,
    lastError: hostWsErr,
  } = useAdminHostMetricsWs(wsEnabled);

  if (loading) {
    return (
      <div className="admin-overview-loading">
        <Spin size="large" tip="加载汇总数据…" />
      </div>
    );
  }

  if (err === "forbidden") {
    return (
      <Alert
        type="error"
        showIcon
        message="无权限"
        description="当前账号不在管理后台白名单内（见 backend ADMIN_CONSOLE_USER_IDS / sql/014_bootstrap_users.sql）。"
      />
    );
  }

  if (err === "error" || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="无法加载总览"
        description="请确认后端已启动，且 Vite 代理 /api 指向正确端口（默认 8010）。"
      />
    );
  }

  return (
    <div className="admin-ledger-page admin-ledger-page--overview-scroll">
      <div className="luminous-dash luminous-dash--scroll">
        <header className="luminous-dash__hero">
          <h1>系统概览</h1>
          <p className="luminous-dash__hero-lead">
            业务汇总与节点运行状态；部署后即反映当前服务器上的指标。
            <span
              className={`luminous-dash__ws-tag luminous-dash__ws-tag--${hostWsStatus}`}
            >
              {" "}
              资源推送 {hostWsStatusLabel[hostWsStatus] ?? hostWsStatus}
              {hostWsErr ? ` · ${hostWsErr}` : ""}
            </span>
          </p>
        </header>

        <div className="luminous-dash__kpi-row">
          <div className="luminous-stat">
            <span className="luminous-stat__label">用户总数</span>
            <div className="luminous-stat__row">
              <span className="luminous-stat__value">
                {fmtCompact(data.users_total)}
              </span>
            </div>
          </div>
          <div className="luminous-stat">
            <span className="luminous-stat__label">订单量</span>
            <div className="luminous-stat__row">
              <span className="luminous-stat__value">
                {fmtCompact(data.orders_total)}
              </span>
            </div>
          </div>
          <div className="luminous-stat">
            <span className="luminous-stat__label">活跃订阅</span>
            <div className="luminous-stat__row">
              <span className="luminous-stat__value">
                {fmtCompact(data.subscriptions_active)}
              </span>
            </div>
          </div>
          <div className="luminous-stat">
            <span className="luminous-stat__label">监管数据项</span>
            <div className="luminous-stat__row">
              <span className="luminous-stat__value">
                {fmtCompact(data.regulatory_items_total)}
              </span>
            </div>
          </div>
        </div>

        <div className="luminous-dash__row-12">
          <div className="luminous-dash__col-main">
            <div className="luminous-dash__chart-head luminous-dash__chart-head--spec">
              <h2>业务趋势</h2>
              <div className="luminous-legend luminous-legend--spec">
                <span>
                  <span className="dot" style={{ background: "#00bcd4" }} />
                  支付订单
                </span>
                <span>
                  <span className="dot" style={{ background: "#4858ab" }} />
                  新注册用户
                </span>
              </div>
            </div>
            <div className="luminous-panel-chart">
              <div className="luminous-panel-chart__echarts">
                <LuminousBusinessTrendDual
                  points={last7.filter((p) => !p.date.startsWith("pad-"))}
                />
              </div>
            </div>
            <p className="luminous-panel-chart__footnote">
              近 7 个 UTC 日历日，与总览接口一致。
            </p>

            <div className="luminous-dash__chart-head luminous-dash__chart-head--spec luminous-dash__chart-head--stacked">
              <h2>服务器 CPU</h2>
              <div className="luminous-legend luminous-legend--spec">
                <span>
                  <span className="dot" style={{ background: "#00bcd4" }} />
                  CPU 占用
                </span>
              </div>
            </div>
            <div className="luminous-panel-chart luminous-panel-chart--stacked">
              <div className="luminous-panel-chart__echarts luminous-panel-chart__echarts--short">
                <LuminousHostCpuTrend points={hostSeries} height={260} />
              </div>
            </div>
            <p className="luminous-panel-chart__footnote">
              当前 API 所在服务器；内存见右侧环形图。横轴 UTC 采样，约每 2 秒一帧。
            </p>
          </div>

          <aside className="luminous-dash__col-side">
            <div className="luminous-quota-card">
              <h3 className="luminous-quota-card__title">AI 使用配额</h3>
              <LuminousAiQuotaDonut
                usedMonth={usedMonth}
                budget={budget ?? null}
              />
            </div>

            <div className="luminous-quota-card luminous-quota-card--server">
              <h3 className="luminous-quota-card__title">服务器内存</h3>
              {hostMetrics ? (
                <LuminousHostMemoryDonut
                  percent={hostMetrics.memory.percent}
                  caption={`${fmtBytes(hostMetrics.memory.used_bytes)} / ${fmtBytes(hostMetrics.memory.total_bytes)}（实时）`}
                />
              ) : (
                <p className="luminous-donut-spec__cap luminous-donut-spec__cap--placeholder">
                  {hostWsStatus === "connecting" || hostWsStatus === "open"
                    ? "等待推送…"
                    : hostWsErr || "暂无数据"}
                </p>
              )}
            </div>

            <div className="luminous-dash__feedback luminous-dash__feedback--spec">
              <h3 className="luminous-dash__feedback-title">最近反馈</h3>
              <div className="luminous-feedback-list luminous-feedback-list--spec">
                {feedback.length === 0 ? (
                  <div className="luminous-feedback-card luminous-feedback-card--spec">
                    <p className="luminous-feedback-empty">暂无最近工单</p>
                  </div>
                ) : (
                  feedback.map((row, i) => (
                    <div
                      key={row.id}
                      className="luminous-feedback-card luminous-feedback-card--spec"
                    >
                      <div
                        className={`luminous-feedback-av ${i % 2 === 0 ? "luminous-feedback-av--indigo" : "luminous-feedback-av--cyan"}`}
                      >
                        <UserOutlined />
                      </div>
                      <div>
                        <strong>
                          {row.contact?.trim() ||
                            `用户 ${row.user_id.slice(0, 8)}…`}
                        </strong>
                        <p className="luminous-feedback-quote">
                          {row.question.length > 120
                            ? `「${row.question.slice(0, 120)}…」`
                            : `「${row.question}」`}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>
        </div>

        <div className="luminous-dash__llm luminous-dash__llm--spec">
          <div className="luminous-dash__llm-top">
            <div>
              <h2>LLM 配置</h2>
              <p>校准智能用量结构（数值来自本月合规深扫统计；推理超参由后端环境统一管理）。</p>
            </div>
            <div className="luminous-dash__llm-actions">
              <button
                type="button"
                className="luminous-btn-ghost"
                onClick={() =>
                  message.info("推理采样参数由后端 LLM 环境变量统一配置，不在此页面修改。")
                }
              >
                重置默认
              </button>
              <button type="button" className="luminous-btn-solid" onClick={() => void load()}>
                刷新数据
              </button>
            </div>
          </div>

          <div className="luminous-dash__sliders">
            <div className="luminous-slider-block">
              <div className="luminous-slider-head">
                <label>月度配额使用</label>
                <span className="val val--cyan">
                  {budgetValDisplay != null ? budgetValDisplay.toFixed(2) : "—"}
                </span>
              </div>
              <div className="luminous-slider-track">
                <div
                  className="luminous-slider-fill luminous-slider-fill--cyan"
                  style={{ width: `${budgetPct}%` }}
                />
                <div
                  className="luminous-slider-knob"
                  style={{ left: `${budgetPct}%` }}
                />
              </div>
              <p className="luminous-slider-hint">
                本月已消耗 Token 占 LLM_MONTHLY_TOKEN_BUDGET 的比例；未配置预算时进度条为示意。
              </p>
            </div>

            <div className="luminous-slider-block">
              <div className="luminous-slider-head">
                <label>提示令牌占比</label>
                <span className="val val--indigo">
                  {promptRatio.toFixed(2)}
                </span>
              </div>
              <div className="luminous-slider-track">
                <div
                  className="luminous-slider-fill luminous-slider-fill--indigo"
                  style={{ width: `${promptRatio * 100}%` }}
                />
                <div
                  className="luminous-slider-knob luminous-slider-knob--indigo"
                  style={{ left: `${promptRatio * 100}%` }}
                />
              </div>
              <p className="luminous-slider-hint">
                输入侧（prompt）Token 占本月有 usage 记录的总 Token 比例。
              </p>
            </div>

            <div className="luminous-slider-block">
              <div className="luminous-slider-head">
                <label>补全令牌占比</label>
                <span className="val" style={{ color: "#191c1e" }}>
                  {completionRatio.toFixed(2)}
                </span>
              </div>
              <div className="luminous-slider-track">
                <div
                  className="luminous-slider-fill luminous-slider-fill--dark"
                  style={{ width: `${completionRatio * 100}%` }}
                />
                <div
                  className="luminous-slider-knob luminous-slider-knob--dark"
                  style={{ left: `${completionRatio * 100}%` }}
                />
              </div>
              <p className="luminous-slider-hint">
                输出侧（completion）Token 占本月有 usage 记录的总 Token 比例。
              </p>
            </div>
          </div>

          {llm?.model ? (
            <p className="luminous-slider-hint" style={{ marginTop: "1.25rem" }}>
              当前模型：<strong>{llm.model}</strong>
              {data.generated_at
                ? ` · 数据时间 ${new Date(data.generated_at).toLocaleString("zh-CN")}`
                : ""}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
